import { useContext, useEffect, useState } from "react";
import { RecordsContext } from "../records/Records";
import { Box, Button, Unstable_Grid2 as Grid, IconButton } from "@mui/material";
import { useForm, useFieldArray, UseFormReturn, ErrorOption } from "react-hook-form";
import { Delete } from "@mui/icons-material";
import { FilterOperator } from "./FilterOperator";
import { FilterFieldValue } from "./FilterFieldValue";
import { FilterField } from "./FilterField";
import { FilterType } from "./FilterType";
import { useStore } from "../../store/store";
import { FormatedField } from "../../types/fields.types";
import { isEmpty, isArray } from "lodash";
import { normalizeDynamicDateFilters, DYNAMIC_AND_STATIC_DATE_OPERATORS, isDynamicDateValue } from "./dynamic-fields/dynamicDateUtils";
import { arrayHasDuplicates } from "../../utils/arrayUtils";

type FilterCondition = "_and" | "_or";
export type WatchedFieldsType = {
	[x: string]: any;
};

type FilterProps = {
	setFilterIsOpen: (value: boolean) => void;
};

export const Filters = ({ setFilterIsOpen }: FilterProps) => {
	const context = useContext(RecordsContext);
	const [recordsFilter, setRecordsFilter, clearEntityFilters, modalFilter, setModalFilters] = useStore((state) => [
		state.recordsFilter || state.gridSettings.selectedSettings?.config?.filters?.records,
		state.setRecordsFilter,
		state.clearEntityFilters,
		state.modalFilter,
		state.setModalFilters,
	]);

	const filtersFromApi = context?.entity && recordsFilter ? transformFilter(recordsFilter?.[context?.entity]) : null;
	const [filterType, setFilterType] = useState<FilterCondition>(filtersFromApi?.filterType || "_and");
	const entityForm = useForm() as unknown as UseFormReturn;

	const { fields, append, remove } = useFieldArray({
		name: "filters",
		control: entityForm.control,
	});

	useEffect(() => {
		if (modalFilter) {
			entityForm.setValue("filters", modalFilter);
		}
	}, [entityForm, modalFilter]);

	const onSubmit = (data: any) => {
		if (!validateFilters(data.filters, filterType)) return;

		const apiFilters = createApiFilters(filterType, data.filters);

		if (!context?.entity) return;
		setModalFilters(data.filters);

		setRecordsFilter({ ...recordsFilter, [context.entity]: apiFilters });
		setFilterIsOpen(false);
	};

	let formErrors: any[] = [];
	if (entityForm?.formState?.errors?.filters) {
		formErrors = entityForm?.formState?.errors?.filters as unknown as ErrorOption[];
	}

	if (!context) return null;

	return (
		<form id="filters-form" onSubmit={entityForm.handleSubmit(onSubmit)} style={{ marginTop: 10 }}>
			<Box mb={2} maxWidth={400}>
				<FilterType filterType={filterType} setFilterType={setFilterType} />
			</Box>
			<Grid container xs={12} alignItems="center" spacing={2} sx={{ mb: 2 }}>
				{fields.map((field, index) => {
					return (
						<Grid key={field.id} container xs={12} spacing={5}>
							<Grid xs={4}>
								<FilterField
									index={index}
									entityForm={entityForm}
									formErrors={formErrors}
									context={context}
								/>
							</Grid>
							<Grid xs={3}>
								{context && (
									<FilterOperator
										context={context}
										entityForm={entityForm}
										index={index}
										formErrors={formErrors}
									/>
								)}
							</Grid>
							<Grid xs={4}>
								{context && (
									<FilterFieldValue context={context} index={index} entityForm={entityForm} />
								)}
							</Grid>
							<Grid xs={1} sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
								<IconButton
									sx={{ mt: 2 }}
									type="button"
									onClick={() => {
										remove(index);
									}}
								>
									<Delete />
								</IconButton>
							</Grid>
						</Grid>
					);
				})}

				<Grid>
					<Button
						type="button"
						onClick={() =>
							append({
								field: "",
								operator: "",
							})
						}
					>
						Adicionar Filtro
					</Button>
				</Grid>
				<Grid>
					<Button
						onClick={() => {
							remove();
							clearEntityFilters(context.entity);
						}}
						color="secondary"
					>
						Limpar Filtros
					</Button>
				</Grid>
			</Grid>
		</form>
	);
};

export const OPERATORS_WITH_TRUE_AS_VALUE = ["_empty", "_nempty", "_null", "_nnull"];

export type Filter = { field: FormatedField; operator: string; value: any };

const sanitizeValue = (value: any, field: FormatedField) => {
	if (value && value.id) return value.id;

	return value;
};

const handleManyToManyFields = (filter: Filter) => {
	const field = filter.field;
	const operator = filter.operator;
	const value = filter.value.map((value: { id: number; label: string }) => value.id);

	return {
		[field.field]: {
			[`${field.settings.relatedTable}_id`]: {
				id: {
					[operator]: value,
				},
			},
		},
	};
};

const ARRAY_VALUE_CONTAINS_OPERATORS = ["_contains", "_ncontains"];

const handleOtherFields = (filter: Filter) => {

    const mapUserFieldsToApiPath = (fieldName: string) => {
        const userFieldMapping: { [key: string]: string } = {
            user_first_name: "user_id.first_name",
            user_last_name: "user_id.last_name",
            user_email: "user_id.email",
            user_phone: "user_id.phone",
            user_avatar: "user_id.avatar",
            client_user_id: "user_id.id",
        };

        const mappedField = userFieldMapping[fieldName];

        return mappedField || fieldName;
    };

	const fieldName = filter.field.field;
	const apiFieldPath = mapUserFieldsToApiPath(fieldName);
	const pathParts = apiFieldPath.split(".");

	const buildClause = (singleValue: any) => {
		if (pathParts.length === 2) {
			return {
				[pathParts[0]]: {
					[pathParts[1]]: {
						[filter.operator]: sanitizeValue(singleValue, filter.field),
					},
				},
			};
		}
		return {
			[fieldName]: {
				[filter.operator]: sanitizeValue(singleValue, filter.field),
			},
		};
	};

	// CSV multi-select: Directus _contains / _ncontains only accept a single
	// primitive, so an array value has to be split into multiple clauses.
	if (ARRAY_VALUE_CONTAINS_OPERATORS.includes(filter.operator) && Array.isArray(filter.value)) {
		const isExclusion = filter.operator === "_ncontains";
		const groupOperator = isExclusion ? "_and" : "_or";
		return { [groupOperator]: filter.value.map(buildClause) };
	}

	return buildClause(filter.value);
};

const handleOrdersPayments = (filter: Filter) => {
    return {
        orders_payments: {
            payment_method: {
                [filter.operator]: filter.value,
            },
        },
    };
};

const handleOrdersPaymentsDate = (filter: Filter) => {
    return {
        orders_payments: {
            payment_date: {
                [filter.operator]: filter.value,
            },
        },
    };
};

/**
 * Separates normalized filters into two groups: grouped and ungrouped.
 * 
 * Filters with a `groupId` need to be ANDed together (e.g., because a filter like "equal to this week" expands into _gte and _lte filters).
 * Filters without a `groupId` are regular filters that should be processed individually.
 */
const separateFiltersIntoGroups = (normalizedFilters: Array<Filter & { groupId?: string }>) => {
	const groupedFilters: Map<string, Array<Filter & { groupId: string }>> = new Map();
	const ungroupedFilters: Array<Filter> = [];

	normalizedFilters.forEach((filter) => {
		if (filter.groupId) {
			const groupedFilter = filter as Filter & { groupId: string };
			const existingGroup = groupedFilters.get(filter.groupId);
			if (existingGroup) {
				existingGroup.push(groupedFilter);
			} else {
				groupedFilters.set(filter.groupId, [groupedFilter]);
			}
		} else {
			ungroupedFilters.push(filter);
		}
	});

	return { groupedFilters, ungroupedFilters };
};

const processFilters = (
	groupedFilters: Map<string, Array<Filter & { groupId: string }>>,
	ungroupedFilters: Array<Filter>,
) => {
	type ApiFilterObject = Record<string, any>;
	type AndGroup = { _and: Array<ApiFilterObject> };

	const processedFilters: Array<ApiFilterObject | AndGroup> = [];

	for (const group of groupedFilters.values()) {
		const groupApiFilters = group.map((filter) => {
			const { groupId, ...filterWithoutGroupId } = filter;
			
			if (filterWithoutGroupId.field.settings.interface === "list-m2m" || filterWithoutGroupId.field.settings.type === "manytoany") {
				return handleManyToManyFields(filterWithoutGroupId);
            } else {
				return handleOtherFields(filterWithoutGroupId);
			}
		});

		processedFilters.push({ _and: groupApiFilters });
	}

	ungroupedFilters.forEach((filter: Filter) => {
		if (filter.field.settings.interface === "list-m2m" || filter.field.settings.type === "manytoany") {
			processedFilters.push(handleManyToManyFields(filter));
		} else if (filter.field.field === "orders_payments_methods") {
			processedFilters.push(handleOrdersPayments(filter));
		} else if (filter.field.field === "orders_payments_date") {
			processedFilters.push(handleOrdersPaymentsDate(filter));
		} else {
			processedFilters.push(handleOtherFields(filter));
		}
	});

	return processedFilters;
};

const createApiFilters = (filterType: string, filters: Array<Filter>) => {
	const normalizedfiltersWithTrueAsValue = normalizeFiltersThatHaveTrueAsValue(filters);

	const normalizedFilters = normalizeDynamicDateFilters(normalizedfiltersWithTrueAsValue);

	const { groupedFilters, ungroupedFilters } = separateFiltersIntoGroups(normalizedFilters);

	const processedFilters = processFilters(groupedFilters, ungroupedFilters);

	const apiFilters = {
		[filterType]: processedFilters,
	};

	return apiFilters;
};

function transformFilter(input: { [x: string]: any } | undefined) {
	if (!input) return { filters: [], filterType: "_and" as FilterCondition };
	let filterType = Object.keys(input)[0] as FilterCondition; // get the "_and" key or equivalent
	let filterData = input[filterType]; // get the array containing the filters

	let filters = filterData.map((filter: any) => {
		let field = Object.keys(filter)[0]; // get the field name (e.g. "first_name")
		let operator = Object.keys(filter[field])[0]; // get the operator (e.g. "_icontains")
		let value = filter[field][operator]; // get the value (e.g. "pedro")

		return { field, operator, value };
	});

	return { filterType, filters };
}

function validateFilters(filters: Array<Filter>, filterType: string) {
	if (filters.length === 0) {
		alert("Por favor selecione campos para filtrar");
		return;
	}

	for (let filter of filters) {
		if (!filter.field || isEmpty(filter.field)) {
			alert(`Por favor selecione pelo menos um campo`);
			return false;
		}

		if (!filter.operator || isEmpty(filter.operator)) {
			alert(`Por favor selecione um operador ${filter.field.headerName}`);
			return false;
		}
        const hasRepeatedFields = arrayHasDuplicates(filters.map((filter) => filter.field.field));
		const isDynamicDateFilter = isDynamicDateValue(filter.value);
        if (filterType === "_and" && hasRepeatedFields && isDynamicDateFilter) {
            alert("A combinação do tipo de filtro 'Todas as condições tem que ser verdade' com filtros relativos, não permite repetir o mesmo campo");
            return false;
        }

		const operatorsWithoutValue = [...OPERATORS_WITH_TRUE_AS_VALUE, ...DYNAMIC_AND_STATIC_DATE_OPERATORS];
		if (!operatorsWithoutValue.includes(filter.operator)) {
			if (filter.value === null || filter.value === "" || (isArray(filter.value) && isEmpty(filter.value))) {
				alert(`A opção não pode estar vazia: ${filter.field.headerName}`);
				return false;
			}
		}
	}
	return true;
}

const normalizeFiltersThatHaveTrueAsValue = (filters: Array<Filter>) => {
	return filters.map((filter) => {
		if (OPERATORS_WITH_TRUE_AS_VALUE.includes(filter.operator)) {
			return { ...filter, value: true };
		}
		return filter;
	});
}